/* ═══════════════════════════════════════════════════════════════
   HEALTH STRIP — the pure helpers behind the five-cell strip and
   the page header above it (design 2026-09-14 front-door pass §4).

   No React, no fetch, no Date. Every function returns '' (or a
   safe default) on null/undefined/rubbish and never throws — the
   strip must degrade to a blank word, never to a crash.
   ═══════════════════════════════════════════════════════════════ */

const num = (x) => (typeof x === 'number' && Number.isFinite(x) ? x : null);

/**
 * A 28-day trend, as words the manager reads instead of an arrow:
 *   ≤ −45 'down by half'   ≤ −25 'down a third'   ≤ −10 'down a little'
 *   |pct| < 10 'no change'
 *   ≥ 10 'up a little'     ≥ 25 'up a quarter'    ≥ 45 'up by half'
 * '' on null/undefined/anything that is not a finite number.
 */
export function trendWords(pct) {
  const p = num(pct);
  if (p === null) return '';
  if (p <= -45) return 'down by half';
  if (p <= -25) return 'down a third';
  if (p <= -10) return 'down a little';
  if (p < 10) return 'no change';
  if (p >= 45) return 'up by half';
  if (p >= 25) return 'up a quarter';
  return 'up a little';
}

/**
 * The trend's tone for a "lower is better" count: 'worse' when it is
 * up (≥ 10), 'better' when it is down (≤ −10), 'same' inside that band,
 * '' when there is no trend to speak of. Colour is a fact about the
 * trend, never decoration, so the tone is decided here and the screen
 * only maps it to a token.
 */
export function trendTone(pct) {
  const p = num(pct);
  if (p === null) return '';
  if (p >= 10) return 'worse';
  if (p <= -10) return 'better';
  return 'same';
}

/** '19 vehicles', '1 driver', '0 vehicles'. Rubbish counts read as 0. */
export function countNoun(n, singular, plural = `${singular}s`) {
  const k = Math.max(0, Math.round(num(n) ?? 0));
  return `${k.toLocaleString()} ${k === 1 ? singular : plural}`;
}

/**
 * The org's name for the page title: the current membership's name
 * (the switcher's source of truth), else the login payload's
 * org_name, else ''. Never a placeholder — the caller decides what
 * to show when there is no org.
 */
export function orgDisplayName(user) {
  if (!user || typeof user !== 'object') return '';
  const memberships = Array.isArray(user.memberships) ? user.memberships : [];
  const current = memberships.find((m) => m && m.organisation_id === user.org_id);
  const name = (current && current.name) || user.org_name || '';
  return typeof name === 'string' ? name.trim() : '';
}
